// Fee tier structure for each exchange
export interface ExchangeFeeOverride {
  maker: { USDT?: number; BTC?: number; KRW?: number };
  taker: { USDT?: number; BTC?: number; KRW?: number };
}

// Exchange enum for opportunity filters (matches futuresSpot.ts)
export enum OpportunityExchange {
  Binance = "Binance",
  Upbit = "Upbit",
  Bithumb = "Bithumb",
  BinanceFutures = "BinanceFutures",
}

export interface OpportunityFilters {
  // Diff filter
  diffFilterEnabled: boolean;
  diffFilterMin: number; // minimum % diff
  // Funding rate filter
  fundingFilterEnabled: boolean;
  fundingFilterMin: number; // minimum funding rate %
  // Exchange filters
  exchangeFilters: Record<OpportunityExchange, boolean>;
  // Quote currency filters
  btcQuoteEnabled: boolean;
  usdtQuoteEnabled: boolean;
  // Hide active positions
  showActivePositions: boolean;
  // Excluded symbols (base assets)
  excludedSymbols: string[];
}

export interface Settings {
  version: number;
  filters: {
    excludedTokens: string[];
    minProfitPercent: number;
    maxProfitPercent: number | null; // null means no maximum limit
    minVolumeUSD: number;
    tradeAmountUSD: number; // Maximum amount in USD to use per trade (0 = unlimited)
    networkStatus: {
      enabled: boolean; // Whether to filter by network status
      requireBothEnabled: boolean; // If true, both withdraw and deposit must be enabled
      hideUnknown: boolean; // If true, hide opportunities with unknown network status
      hideDisabled: boolean; // If true, hide opportunities where either withdraw or deposit is disabled
    };
  };
  // Opportunity filters for position opportunities table
  opportunityFilters: OpportunityFilters;
  // Separate exchange filters for buy and sell
  buyExchanges: {
    [exchangeIndex: number]: boolean;
  };
  sellExchanges: {
    [exchangeIndex: number]: boolean;
  };
  // Custom fee overrides (optional - uses default if not set)
  feeOverrides?: {
    [exchangeName: string]: ExchangeFeeOverride;
  };
  display: {
    sortBy: "profit_desc" | "created_desc" | "updated_desc";
    switchToActivePositionsAfterStart: boolean;
  };
  features: {
    enableFutures: boolean; // Whether to display Binance Futures data and subscribe to WebSocket
  };
  strategyBudget: number; // Strategy execution budget in USD
  leverage: number; // Futures leverage for hedge strategies (1-50, default 2)
  useMultiTierProfit: boolean; // Whether to use multi-tier profit strategy (false = single tier)
  profitTiers: Array<{
    profit_percentage: number; // e.g., 0.05 for 5%
    volume_percentage_to_sell: number; // e.g., 0.30 for 30%, 0 for remaining
  }>;
  entryDiffPercent: number; // Entry diff % for StandbyHedged strategy (e.g., -0.5)
  hedgeConfig: {
    priceSurgeThreshold: number; // Price surge threshold % (e.g., 50 for 50%)
    liquidationProximity: number; // Liquidation proximity % (e.g., 90 for 90%)
    entryFundingRate: number | null; // Minimum funding rate % required to start/add entry
    entryFundingIntervalHours: 0 | 1 | 4 | 8; // Required rounded funding interval in hours (0 = don't care)
    spotFuturesGapMagnitude: number; // Signed spot/futures gap threshold % (e.g., -3 for -3%)
    lowFundingRate: number; // Low funding rate % (e.g., -0.5 for -0.5%)
    fundingTimeWindow: number; // Funding time window in seconds (e.g., 600)
  };
  buyOpenMode: "Limit" | "Market"; // BuyOpen order mode (default: Limit)
  marketNotionalUsdt: number; // Max notional USDT per BuyOpen task in Market mode (min: 5)
  replayEnabled: boolean; // Whether standby strategies should auto-replay after completion
  replayDelaySeconds: number; // Delay before replay attempts start
}

export const DEFAULT_OPPORTUNITY_FILTERS: OpportunityFilters = {
  diffFilterEnabled: false,
  diffFilterMin: 0.5,
  fundingFilterEnabled: false,
  fundingFilterMin: 0.01,
  exchangeFilters: {
    [OpportunityExchange.Binance]: true,
    [OpportunityExchange.Upbit]: true,
    [OpportunityExchange.Bithumb]: true,
    [OpportunityExchange.BinanceFutures]: true,
  },
  btcQuoteEnabled: true,
  usdtQuoteEnabled: true,
  showActivePositions: true,
  excludedSymbols: [],
};

export const DEFAULT_SETTINGS: Settings = {
  version: 2,
  filters: {
    excludedTokens: [],
    minProfitPercent: 0.1,
    maxProfitPercent: null, // Default to no maximum limit
    minVolumeUSD: 100,
    tradeAmountUSD: 0, // Default to 0 (unlimited)
    networkStatus: {
      enabled: true, // Default to filtering by network status
      requireBothEnabled: true, // Default to requiring both enabled
      hideUnknown: false, // Default to showing unknown status
      hideDisabled: true, // Default to hiding disabled networks
    },
  },
  opportunityFilters: DEFAULT_OPPORTUNITY_FILTERS,
  // All exchanges enabled for buying by default
  buyExchanges: {
    0: true, // Upbit
    1: true, // Bithumb
    2: true, // Coinone
    3: true, // Binance
    4: true, // HTX
    5: true, // Gate.io
    6: true, // KuCoin
    7: true, // OKX
    8: true, // MEXC
    9: true, // Bybit
    10: true, // Bitget
    // 11: false   // Coinbase - Not supported yet
  },
  // All exchanges enabled for selling by default
  sellExchanges: {
    0: true, // Upbit
    1: true, // Bithumb
    2: true, // Coinone
    3: true, // Binance
    4: true, // HTX
    5: true, // Gate.io
    6: true, // KuCoin
    7: true, // OKX
    8: true, // MEXC
    9: true, // Bybit
    10: true, // Bitget
    // 11: false   // Coinbase - Not supported yet
  },
  // No fee overrides by default - use standard fees
  feeOverrides: undefined,
  display: {
    sortBy: "profit_desc",
    switchToActivePositionsAfterStart: true,
  },
  features: {
    enableFutures: true, // Enabled by default
  },
  strategyBudget: 10.0, // Default $10 USD budget
  leverage: 2, // Default 2x leverage
  useMultiTierProfit: false, // Default to single tier mode
  profitTiers: [
    { profit_percentage: 0.02, volume_percentage_to_sell: 0.3 }, // 2% profit → sell 30%
    { profit_percentage: 0.05, volume_percentage_to_sell: 0.4 }, // 5% profit → sell 40%
    { profit_percentage: 0.1, volume_percentage_to_sell: 0.3 }, // 10% profit → sell 30%
  ],
  entryDiffPercent: -0.5, // Default -0.5% entry diff
  hedgeConfig: {
    priceSurgeThreshold: 50, // Default 50%
    liquidationProximity: 90, // Default 90%
    entryFundingRate: null,
    entryFundingIntervalHours: 8,
    spotFuturesGapMagnitude: -3, // Default -3%
    lowFundingRate: -0.5, // Default -0.5%
    fundingTimeWindow: 600, // Default 600 seconds
  },
  buyOpenMode: "Limit", // Default to Limit mode
  marketNotionalUsdt: 5, // Default $5 minimum
  replayEnabled: false,
  replayDelaySeconds: 300,
};
